import { Route, Units } from '../../services/api.types'
import {
  celsiusToFahrenheit,
  getElevationUnit,
  getPaceUnit,
  getSpeedUnit,
  getTemperatureUnit,
  metersToFeet,
  mpsToKph,
  mpsToMph,
} from '../../common/helpers/unitFormatters'

/**
 * The chartable series: heart rate, a tempo series (pace for foot sports, speed for cycling),
 * power, and temperature toggle; altitude is the permanent backdrop.
 */
export type RouteStatKey = 'hr' | 'pace' | 'spd' | 'pwr' | 'tmp' | 'alt'

/** The tempo representation for a sport: min/km-style pace, or km/h-style speed (cycling). */
export type TempoKey = 'pace' | 'spd'

export interface SeriesPoint {
  /** Elapsed workout seconds since the first route point. */
  x: number
  /** Value in display units; null marks a gap so the line breaks there. */
  y: number | null
}

export interface RouteSeries {
  points: SeriesPoint[]
  yUnit: string
  /** Min/max over the defined values, in display units. */
  min: number
  max: number
  /** Duration covered by the route, in seconds. */
  totalSeconds: number
}

/** The route-point field backing each series (pace and speed both derive from speed samples). */
const SERIES_FIELD: Record<RouteStatKey, 'hr' | 'spd' | 'pwr' | 'tmp' | 'alt'> = {
  hr: 'hr',
  pace: 'spd',
  spd: 'spd',
  pwr: 'pwr',
  tmp: 'tmp',
  alt: 'alt',
}

// Below this speed pace is meaningless (standing still tends to infinite pace), so such samples
// become gaps. 0.5 m/s caps pace at 33:20 min/km.
const MIN_PACE_SPEED_MPS = 0.5

// Downsampling: Garmin pushes ~1 sample/sec, so a 2h ride is ~7200 points — far more SVG nodes
// than Victory can animate smoothly. Above the threshold, points are averaged into fixed
// elapsed-time buckets; ~200 points is indistinguishable at card width.
const TARGET_BUCKETS = 200
const DOWNSAMPLE_THRESHOLD = 300

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)

// Haversine distance in meters between two route points.
const distanceBetween = (a: Route, b: Route): number => {
  const R = 6371000
  const φ1 = (a.lat * Math.PI) / 180
  const φ2 = (b.lat * Math.PI) / 180
  const Δφ = ((b.lat - a.lat) * Math.PI) / 180
  const Δλ = ((b.lng - a.lng) * Math.PI) / 180
  const h = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
}

/**
 * Recorders commonly deliver the first GPS fixes without a speed value (speed is unknown until
 * GPS converges), which would make the pace/speed line start visibly late. Fill missing samples
 * from the track itself — distance/time to the previous point — with the first point borrowing
 * the following segment's value so the line reaches the start of the chart.
 */
export const fillMissingSpeeds = (routes: Route[]): Route[] => {
  if (routes.every((point) => isFiniteNumber(point.spd))) return routes
  const sorted = [...routes].sort((a, b) => a.ts - b.ts)
  const filled = sorted.map((point, i) => {
    if (isFiniteNumber(point.spd) || i === 0) return point
    const prev = sorted[i - 1]
    const seconds = (point.ts - prev.ts) / 1000
    return seconds > 0 ? { ...point, spd: distanceBetween(prev, point) / seconds } : point
  })
  if (filled.length > 1 && !isFiniteNumber(filled[0].spd) && isFiniteNumber(filled[1].spd)) {
    filled[0] = { ...filled[0], spd: filled[1].spd }
  }
  return filled
}

/**
 * The series that have enough data to chart (≥2 defined points), in display order. The tempo
 * series is offered as pace by default, or as speed when the sport calls for it (cycling).
 */
export const detectAvailableSeries = (routes: Route[], tempo: TempoKey = 'pace'): RouteStatKey[] => {
  const priority: RouteStatKey[] = ['hr', tempo, 'pwr', 'tmp', 'alt']
  const counts: Record<string, number> = { hr: 0, [tempo]: 0, pwr: 0, tmp: 0, alt: 0 }
  routes.forEach((point) => {
    priority.forEach((key) => {
      if (isFiniteNumber(point[SERIES_FIELD[key]])) counts[key]++
    })
  })
  return priority.filter((key) => counts[key] >= 2)
}

// Pace works on raw m/s until after bucket averaging (averaging paces directly would skew
// towards slow samples); the mean speed is converted to pace as a final step.
const convertValue = (key: RouteStatKey, value: number, userUnits: Units): number => {
  if (key === 'alt') return userUnits === Units.IMPERIAL ? metersToFeet(value) : value
  if (key === 'spd') return userUnits === Units.IMPERIAL ? mpsToMph(value) : mpsToKph(value)
  if (key === 'tmp') return userUnits === Units.IMPERIAL ? celsiusToFahrenheit(value) : value
  return value // hr (bpm) and pwr (watts) are unit-system independent; pace converts after averaging
}

export const getSeriesUnit = (key: RouteStatKey, userUnits: Units): string => {
  if (key === 'pace') return getPaceUnit(userUnits)
  if (key === 'spd') return getSpeedUnit(userUnits)
  if (key === 'alt') return getElevationUnit(userUnits)
  if (key === 'pwr') return 'W'
  if (key === 'tmp') return getTemperatureUnit(userUnits)
  return 'bpm'
}

/** Formats a real (converted) value for display: pace as m:ss, speed with a decimal, hr/alt rounded. */
export const formatSeriesValue = (key: RouteStatKey, value: number): string => {
  if (key === 'pace') {
    const total = Math.round(value)
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
  }
  if (key === 'spd') return value.toFixed(1)
  return String(Math.round(value))
}

/** Builds one chartable series from raw route points: elapsed x, converted y, downsampled. */
export const buildSeries = (routes: Route[], key: RouteStatKey, userUnits: Units): RouteSeries => {
  const field = SERIES_FIELD[key]
  const sorted = [...routes].sort((a, b) => a.ts - b.ts)
  const t0 = sorted.length ? sorted[0].ts : 0
  const totalSeconds = sorted.length ? (sorted[sorted.length - 1].ts - t0) / 1000 : 0
  const defined = sorted.filter((point) => isFiniteNumber(point[field]))

  let points: SeriesPoint[]
  if (defined.length <= DOWNSAMPLE_THRESHOLD || totalSeconds <= 0) {
    points = defined.map((point) => ({
      x: (point.ts - t0) / 1000,
      y: convertValue(key, point[field] as number, userUnits),
    }))
  } else {
    const bucketLen = totalSeconds / TARGET_BUCKETS
    const sums = new Array(TARGET_BUCKETS).fill(0)
    const counts = new Array(TARGET_BUCKETS).fill(0)
    defined.forEach((point) => {
      const x = (point.ts - t0) / 1000
      const bucket = Math.min(Math.floor(x / bucketLen), TARGET_BUCKETS - 1)
      sums[bucket] += convertValue(key, point[field] as number, userUnits)
      counts[bucket]++
    })
    // Empty buckets stay null: the sensor dropped out there and the line should show a gap
    // rather than interpolating across it.
    points = sums.map((sum, bucket) => ({
      x: (bucket + 0.5) * bucketLen,
      y: counts[bucket] ? sum / counts[bucket] : null,
    }))
  }

  if (key === 'pace') {
    const metersPerDistanceUnit = userUnits === Units.IMPERIAL ? 1609.344 : 1000
    points = points.map((point) => ({
      x: point.x,
      y: point.y === null || point.y < MIN_PACE_SPEED_MPS ? null : metersPerDistanceUnit / point.y,
    }))
  }

  let min = Infinity
  let max = -Infinity
  points.forEach((point) => {
    if (point.y !== null) {
      min = Math.min(min, point.y)
      max = Math.max(max, point.y)
    }
  })
  // Zero is meaningful for speed: pauses should read as dips towards the bottom.
  if (key === 'spd') min = Math.min(min, 0)
  if (!Number.isFinite(min)) {
    min = 0
    max = 1
  }

  return { points, yUnit: getSeriesUnit(key, userUnits), min, max, totalSeconds }
}

/**
 * Scales a series to [0,scale] over its own range so series with incompatible units (bpm vs pace
 * vs m) can overlay on one chart. `scale` < 1 confines a series to the bottom band of the chart
 * (used for the altitude area, Strava-style). `invert` flips the series so that a LOWER value
 * plots HIGHER — used for pace, where faster (smaller min/km) should read as up. A flat series
 * renders as a midline instead of dividing by zero.
 */
export const normalizeSeries = (series: RouteSeries, scale = 1, invert = false): SeriesPoint[] => {
  const range = series.max - series.min
  return series.points.map((point) => {
    if (point.y === null) return { x: point.x, y: null }
    const t = range === 0 ? 0.5 : (point.y - series.min) / range
    return { x: point.x, y: (invert ? 1 - t : t) * scale }
  })
}

/** The series' real value at the point nearest to elapsed second x (null inside a gap). */
export const valueAtElapsed = (series: RouteSeries, x: number): number | null => {
  let best: SeriesPoint | null = null
  let bestDelta = Infinity
  series.points.forEach((point) => {
    const delta = Math.abs(point.x - x)
    if (delta < bestDelta) {
      bestDelta = delta
      best = point
    }
  })
  return best === null ? null : (best as SeriesPoint).y
}

/** Elapsed-time tick label: h:mm:ss once the workout reaches an hour, m:ss below it. */
export const formatElapsed = (seconds: number, totalSeconds: number): string => {
  const total = Math.max(0, Math.round(seconds))
  const two = (n: number) => String(n).padStart(2, '0')
  if (totalSeconds >= 3600) {
    return `${Math.floor(total / 3600)}:${two(Math.floor(total / 60) % 60)}:${two(total % 60)}`
  }
  return `${Math.floor(total / 60)}:${two(total % 60)}`
}
